import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { authService } from '../../services/api';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Helper to check if user is admin
  // Wait, I need to see where I can use this.
  // Actually I'll just use user.role === 'Administrator' inline or separate var.
  const isAdmin = user?.role === 'Administrator';
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Hide navbar when modal is open
  useModal(showModal, () => setShowModal(false));

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    role: 'Employee',
    status: 'Active',
    password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await authService.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users', error);
      Swal.fire('Error', 'Failed to load users', 'error');
    }
  };

  const handleEdit = async (user) => {
    setSelectedUser(user);
    // Initial populate from list data
    setFormData({
      name: user.name || '',
      username: user.username,
      email: user.email,
      role: user.role,
      status: user.status || 'Active',
      password: '',
      signature: user.signature || ''
    });
    setIsEditing(true);
    setShowModal(true);

    // Fetch fresh data to ensure signature is up to date
    try {
      const response = await authService.getUser(user.id);
      if (response.data) {
        const freshUser = response.data;
        setFormData(prev => ({
          ...prev,
          name: freshUser.name || '',
          username: freshUser.username,
          email: freshUser.email,
          role: freshUser.role,
          status: freshUser.status || 'Active',
          signature: freshUser.signature || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching fresh user data:', error);
    }
  };

  const handleDelete = async (user) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete user "${user.username}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await authService.deleteUser(user.id);
        Swal.fire('Deleted!', 'User has been deleted.', 'success');
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        Swal.fire({
          icon: 'error',
          title: 'Cannot Delete User',
          text: error.response?.data?.error || 'Failed to delete user.',
        });
      }
    }
  };

  const openAddModal = () => {
    setSelectedUser(null);
    setFormData({
      name: '',
      username: '',
      email: '',
      role: 'Employee',
      status: 'Active',
      password: '',
      signature: ''
    });
    setIsEditing(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedUser) {
        // Update
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password; // Don't send empty password

        await authService.updateUser(selectedUser.id, updateData);
        Swal.fire('Success', 'User updated successfully!', 'success');
      } else {
        // Create
        await authService.register(formData);
        Swal.fire('Success', 'User created successfully!', 'success');
      }

      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error(error);
      setShowModal(false); // Close on error
      Swal.fire('Error', isEditing ? 'Failed to update user' : 'Failed to create user', 'error');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'Administrator': 'bg-danger',
      'Manager': 'bg-warning',
      'Employee': 'bg-info',
      'Auditor': 'bg-secondary'
    };
    return badges[role] || 'bg-secondary';
  };

  const filteredUsers = users.filter(user =>
    !searchTerm ||
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            User Management
          </h2>
          <p className="text-muted mb-0">Manage system users and permissions</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openAddModal}>
            <i className="bi bi-plus-circle"></i> Add User
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">All Users</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan="6" className="text-center">No users found</td></tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td><strong>{user.username}</strong></td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${getRoleBadge(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.status === 'Inactive' ? 'bg-danger' : 'bg-success'}`}>
                          {user.status || 'Active'}
                        </span>
                      </td>
                      <td>
                        {isAdmin && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(user)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(user)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEditing ? 'Edit User' : 'Add New User'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. John Doe"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role *</label>
                    <select
                      className="form-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="Employee">Employee</option>
                      <option value="Manager">Manager</option>
                      <option value="Administrator">Administrator</option>
                      <option value="Auditor">Auditor</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{isEditing ? 'Password (leave blank to keep current)' : 'Password *'}</label>
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!isEditing}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Signature</label>
                    {formData.signature && (
                      <div className="mb-2">
                        <img src={formData.signature} alt="Signature" style={{ maxHeight: '60px', border: '1px solid #ddd' }} />
                      </div>
                    )}
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData({ ...formData, signature: reader.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">{isEditing ? 'Update User' : 'Create User'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
